import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { Observable } from 'rxjs';
import { AppConstans } from '../../appConstants';
import { CreateResultDto } from '../../dto/create-result-dto';
import { DrevinaItemDto } from '../../dto/DrevinaItemDto';
import { FormInfoByCisloJrDto } from '../../dto/FormInfoByCisloJrDto';
import { InfoFromCisloUjDto } from '../../dto/InfoFromCisloUjDto';
import { ResultInfoGenericDto } from '../../dto/ResultInfoDto';
import { SelectItem } from '../../interfaces/select-item';
import { CiselnikyService } from '../../services/ciselniky.service';
import { Erma2Service } from '../../services/erma2.service';
import { FileDownloadService } from '../../services/file-download.service';
import { FillFormService } from '../../services/fill-form.service';
import { OznameniSberuService } from '../../services/oznameni-sberu.service';
import { PdsMessageService } from '../../Services/pds-message.service';
import { CiselnikyUtils } from '../../Utils/ciselniky.utils';
import { FormTouchUtil } from '../../Utils/form-touch-util';
import { CjrMaLicenciAsyncValidator } from '../../validators/cjr.ma.licenci.async.validator';
import { DatumSberuOdValidator } from '../../validators/datum.sberu.od.validator';
import { UjAsyncValidator } from '../../validators/uj.async.validator';
import { VlastnikValidator } from '../../validators/vlastnik.validator';
import { FormBase } from '../form-base';

@Component({
  selector: 'app-form-oznameni-sberu',
  templateUrl: './form-oznameni-sberu.component.html',
  styleUrls: ['./form-oznameni-sberu.component.css'],
  standalone: false,
  encapsulation: ViewEncapsulation.None
})
export class FormOznameniSberuComponent extends FormBase implements OnInit {

  readonly semennyRm: number = 1;

  kategorieRMList: SelectItem[];
  druhRMList: SelectItem[];
  typZdrojeRMList: SelectItem[];
  pobockaList: SelectItem[];
  provenienceList: SelectItem[];
  druhDrevinyList: DrevinaItemDto[];

  private _initZadostId: number;
  private _initToken: string;

  override readonly MSG_KEY: string = 'oznameni-sberu-messages';
  constructor(
    private fb: UntypedFormBuilder,
    private oznameniSberuService: OznameniSberuService,
    private ciselnikyService: CiselnikyService,
    private fillFormService: FillFormService,
    private titleService: Title,
    private ujAsyncValidator: UjAsyncValidator,
    private datumSberuOdValidator: DatumSberuOdValidator,
    private licenceValidator: CjrMaLicenciAsyncValidator,
    private vlastnikValidator: VlastnikValidator,
    downloadService: FileDownloadService,
    formTouch: FormTouchUtil,
    erma2Service: Erma2Service,
    private route: ActivatedRoute,
    private ciselnikyUtils: CiselnikyUtils,
    protected pdsMessageService: PdsMessageService
  ) {
    super(downloadService, formTouch, pdsMessageService, erma2Service)

    this.ujAsyncValidator.ujInfoEvent.subscribe((resp: InfoFromCisloUjDto) => {
      if (!this.validationBeforeSubmit) this.completeByEvCisloUj(resp);
    });

    this.licenceValidator.licenceEvent.subscribe((resp: FormInfoByCisloJrDto) => {
      if (!this.validationBeforeSubmit) this.completeByCisloJr(resp);
    });

    this.form = this.fb.group({
      cislo: [''],
      rok: [new Date().getFullYear(), Validators.required],
      pobockaPovereneOsoby: [null, Validators.required],
      dodavatel: this.fb.group({
        jmeno: [''],
        prijmeni: [''],
        datNaroz: [new Date()],
        obchNaz: [''],
        adresaTrvalehoPobytuFo: [''],
        adresaPodnikaniFo: [''],
        adresaSidlaPo: [''],
        ico: [''],
      }),
      licence: this.fb.group({
        cj: [''],
        zeDne: [''],
        nabylaPravniMoci: [''],
        cisloJr: ['', { updateOn: 'blur', validators: [Validators.required], asyncValidators: [this.licenceValidator] }]
      }),
      mistaSberu: ['', Validators.required],
      oblastProvenience: ['', Validators.required],
      druhDreviny: this.fb.group({
        nazevCesky: ['', Validators.required],
        nazevVedecky: ['', Validators.required],
        oznaceniKlonu: ['']
      }),
      repMaterialKategorie: ['', Validators.required],
      druhRepMaterialu: [null, Validators.required],
      typZdrojeRepMaterialu: ['', Validators.required],
      evCisloUj: ['', { updateOn: 'blur', validators: [Validators.required], asyncValidators: [this.ujAsyncValidator] }],
      predpokladanySber: this.fb.group({
        od: ['', { updateOn: 'blur', validators: [Validators.required, this.datumSberuOdValidator] }],
        do: ['', Validators.required]
      }),
      maxMnozstvi: ['', Validators.required],
      vlastnikZdroje: this.fb.group({
        jmeno: [''],
        prijmeni: [''],
        obchNaz: [''],
        adresa: [''],
        telefon: [''],
        email: ['']
      }, { updateOn: 'blur', validators: [this.vlastnikValidator] }),
      souhlasVlastnika: false,
      misto: ['', Validators.required],
      datum: [new Date().toISOString().substring(0, 10)]
    });
  } // constructor

  callService(): Observable<ResultInfoGenericDto<CreateResultDto>> {
    return this.oznameniSberuService.createNew(this.form.value);
  }

  ngOnInit(): void {

    this.route.params.subscribe(params => {
      this._initZadostId = params.zadostId;
      this._initToken = params.token;
      this.setCiselnikyAndData();
    });

    this.titleService.setTitle("ERMA2 - Oznámení o sběru RM");
    this.fillFormService.getPrihlasenyUzivatelInfo().subscribe((resp: ResultInfoGenericDto<FormInfoByCisloJrDto>) => {
      if (resp != void 0 && resp.success) {
        this._setDodavatelData(resp.data);
        this._setCjr(resp?.data?.cisloJr);
      }
    });

    //uj.async.validator se nezavolá, pokud vymažu hodnotu políčka, proto tento changeHandler.
    this.form.get('evCisloUj')?.valueChanges.subscribe(changedValue => {
      if (changedValue == void 0 || changedValue == '') {
        this._clearDataDleUj();
      }
    });
  }

  /**
   * Přetížení tisku formuláře pro zobrazení specifických chyb.
  **/
  onSubmit(isIsds: boolean = false) {
    this._showErrorMessages();

    super.onSubmit(isIsds);
  }

  get vlastnik() { return this.form.get('vlastnikZdroje'); }
  get souhlasVlastnika() { return this.form.get('souhlasVlastnika'); }
  get predpSberOd() { return this.form.get('predpokladanySber')?.get('od'); }

  /**
   * Načte číselníky ze serveru a naplní formulář
   * */
  setCiselnikyAndData() {
    this.ciselnikyService.getAll().subscribe(lists => {
      this.kategorieRMList = lists.kategorieRm;
      this.druhRMList = lists.druhRm;
      this.typZdrojeRMList = lists.typZdroje;
      this.pobockaList = lists.pobocka.filter(x => x.value != AppConstans.popVydalKodMze);
      this.provenienceList = lists.oblastProvenience;
      this.druhDrevinyList = lists.druhDreviny;
      this._loadInitData();
    });
  }

  /**
   * Pokud je zname id zadosti a token, tak nacist data
   * */
  private _loadInitData(): void {
    if (this._initZadostId != void 0 && this._initToken != void 0) {
      this.oznameniSberuService.get(this._initZadostId, this._initToken).subscribe(res => {
        if (res.success) {
          res.data.predpokladanySber.od = res.data.predpokladanySber.od.substring(0, 10);
          res.data.predpokladanySber.do = res.data.predpokladanySber.do.substring(0, 10);
          res.data.datum = res.data.datum.substring(0, 10);
          this.form.patchValue(res.data);
        } else {
          this.pdsMessageService.add({ key: this.MSG_KEY, severity: 'error', text: res.messages[0] })
        }
      });
    }
  }

  /**
   * Dle čísla jednotného registru doplní část údajů o licenci a dodavateli.
  **/
  completeByCisloJr(data: FormInfoByCisloJrDto) {
    this._resetDodavatelData();
    if (data != void 0) {
      this._setDodavatelData(data);
    }
  }


  /**
   * Naplní data dodavatele a licence.
   * @param data
   */
  private _setDodavatelData(data: FormInfoByCisloJrDto) {
    this.form.get('dodavatel')?.patchValue(data);
    this.form.get('licence')?.get('cj').setValue(data?.licence?.cj);
    this.form.get('licence')?.get('nabylaPravniMoci').setValue(data?.licence?.nabylaPravniMoci);
    this.form.get('licence')?.get('zeDne').setValue(data?.licence?.zeDne);
  }


  /**
   * Naplní/vyprázdní číslo jednotného registru.
   * @param cjr
   */
  private _setCjr(cjr: string): void {
    this.form.get('licence')?.get('cisloJr').setValue(cjr);
  }


  /**
   * Reset dat dodavatele a licence.
  **/
  private _resetDodavatelData(): void {
    this.form.get('dodavatel')?.reset();
    this.form.get('licence')?.patchValue({//cisloJr musi byt zachovano
      cj: '',
      zeDne: '',
      nabylaPravniMoci: ''
    });
  }


  /** Po vyplnění čísla UJ doplní relevantní pole. */
  private completeByEvCisloUj(ujInfo: InfoFromCisloUjDto) {
    if (ujInfo != void 0) {
      let druhDreviny = ujInfo.isValidCzCisloUj ? this.druhDrevinyList.find(x => x.zkratka == ujInfo.druhDreviny) : { nazevCesky: '', nazevVedecky: '' };

      this.form.patchValue({
        oblastProvenience: this.ciselnikyUtils.SelectItemToCiselik(this.provenienceList.find(x => x.value == ujInfo.oblastProvenience)),
        repMaterialKategorie: this.ciselnikyUtils.SelectItemToCiselik(this.kategorieRMList.find(x => x.value == ujInfo.repMaterialKategorie)),
        typZdrojeRepMaterialu: this.ciselnikyUtils.SelectItemToCiselik(this.typZdrojeRMList.find(x => x.value == ujInfo.typZdrojeRepMaterialu)),
        druhDreviny: {
          nazevCesky: druhDreviny.nazevCesky,
          nazevVedecky: druhDreviny.nazevVedecky,
        },
      });
    }
    else {
      this._clearDataDleUj();
    }
  }


  /**
   * Vymaže data načítané z UJ.
  **/
  private _clearDataDleUj(): void {
    this.form.patchValue({
      oblastProvenience: '',
      repMaterialKategorie: '',
      typZdrojeRepMaterialu: '',
      druhDreviny: {
        nazevCesky: '',
        nazevVedecky: '',
      },
    });
  }


  /**
   * Handler uživatelova kliknutí na check-box souhlasu vlastníka.
  **/
  souhlasClickHandler(): void {
    this.vlastnik?.reset();
  }

  /**
   * Zobrazení chyb po validaci formuláře.
  **/
  private _showErrorMessages(): void {
    this.pdsMessageService.clear(this.MSG_KEY);

    this.messageServiceFormError(this.MSG_KEY);

    // datum předp. sběru
    if (!this.predpSberOd?.valid && this.predpSberOd?.errors['sberOd'] != void 0) {
      this.pdsMessageService.add({ key: this.MSG_KEY, severity: 'error', text: this.predpSberOd?.errors['sberOd'] });
    }
  }
}
